import React, { useEffect, useState } from 'react';
import { Carousel } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import ImageWithFallback from './ImageWithFallback.tsx';
import { fetchHomeData, fetchMainCategories, fetchSubCategories } from '../services/googleSheets.ts';
import './HomePage.css';
import BusinessPrompt from './BusinessPrompt.tsx';
import { formatUrlText } from '../utils/urlHelpers.ts';

interface SubCategory {
    id: string;
    mainCategoryId: string;
    title: string;
    iconUrl: string;
    url: string;
}

interface MainCategory {
    id: string;
    title: string;
    iconUrl: string;
}

const HomePage = () => {
    const [homeData, setHomeData] = useState<{
        title: string;
        carouselImages: string[];
        whatsappNumber: string;
    }>({ title: '', carouselImages: [], whatsappNumber: '' });

    const [mainCategories, setMainCategories] = useState<MainCategory[]>([]);
    const [subCategories, setSubCategories] = useState<SubCategory[]>([]);
    const [filteredSubCategories, setFilteredSubCategories] = useState<SubCategory[]>([]);
    const [activeMainCategory, setActiveMainCategory] = useState<string>('');
    const [activeMainCategoryTitle, setActiveMainCategoryTitle] = useState<string>('');
    const [isBusinessPromptOpen, setIsBusinessPromptOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    useEffect(() => {
        const loadData = async () => {
            try {
                const [homeResult, categoriesResult] = await Promise.all([
                    fetchHomeData(),
                    fetchMainCategories()
                ]);
                setHomeData(homeResult);
                setMainCategories(categoriesResult);
                if (categoriesResult.length > 0) {
                    const firstCategory = categoriesResult[0];
                    setActiveMainCategory(firstCategory.id);
                    setActiveMainCategoryTitle(firstCategory.title);
                    const subCatsResult = await fetchSubCategories(firstCategory.id);
                    setSubCategories(subCatsResult);
                    setFilteredSubCategories(subCatsResult);
                }
            } catch (error) {
                console.error('Error loading data:', error);
            }
        };
        loadData();
    }, []);

    const handleMainCategoryClick = async (categoryId: string, categoryTitle: string) => {
        setActiveMainCategory(categoryId);
        setActiveMainCategoryTitle(categoryTitle);
        setSearchQuery('');
        try {
            const subCatsResult = await fetchSubCategories(categoryId);
            setSubCategories(subCatsResult);
            setFilteredSubCategories(subCatsResult);
        } catch (error) {
            console.error('Error loading subcategories:', error);
        }
    };

    const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
        const query = event.target.value;
        setSearchQuery(query);
        
        if (!query.trim()) {
            setFilteredSubCategories(subCategories);
            return;
        }

        const filtered = subCategories.filter(category => 
            category.title.toLowerCase().includes(query.toLowerCase())
        );
        setFilteredSubCategories(filtered);
    };

    const handleAddBusiness = () => {
        setIsBusinessPromptOpen(true);
    };

    return (
        <div className="home-container">
            {/* Hero Section */}
            <div className="hero-section">
                <div className="hero-content">
                    <h1 className="main-title">{homeData.title}</h1>
                    <button onClick={handleAddBusiness} className="add-business-button">
                        <span>أضف نشاطك التجاري</span>
                    </button>
                </div>
            </div>

            {/* Carousel Section */}
            {homeData.carouselImages.length > 0 && (
                <div className="carousel-section">
                    <Carousel 
                        className="modern-carousel"
                        indicators={true}
                        interval={3000}
                        fade={true}
                        controls={false}
                    >
                        {homeData.carouselImages.map((image, index) => (
                            <Carousel.Item key={index}>
                                <div className="carousel-card">
                                    <ImageWithFallback
                                        src={image}
                                        alt={`Slide ${index + 1}`}
                                        className="carousel-image"
                                        hideOnError={true}
                                    />
                                    <div className="carousel-overlay">
                                        <div className="carousel-content">
                                            <div className="carousel-number">
                                                {String(index + 1).padStart(2, '0')}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </Carousel.Item>
                        ))}
                    </Carousel>
                </div>
            )}

            {/* Categories Section */}
            <section className="categories-section">
                <div className="categories-header">
                    <div className="main-categories-tabs">
                        {mainCategories.map((category) => (
                            <button
                                key={category.id}
                                className={`main-category-tab ${activeMainCategory === category.id ? 'active' : ''}`}
                                onClick={() => handleMainCategoryClick(category.id, category.title)}
                            >
                                <ImageWithFallback
                                    src={category.iconUrl}
                                    alt={category.title}
                                    className="tab-icon"
                                    hideOnError={true}
                                />
                                <span>{category.title}</span>
                            </button>
                        ))}
                    </div>

                    {/* Search Bar */}
                    <div className="search-bar">
                        <svg className="search-icon" viewBox="0 0 24 24">
                            <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
                        </svg>
                        <input
                            type="text"
                            placeholder="ابحث في الفئات..."
                            value={searchQuery}
                            onChange={handleSearch}
                            className="search-input"
                        />
                        {searchQuery && (
                            <button 
                                className="clear-search" 
                                onClick={() => {
                                    setSearchQuery('');
                                    setFilteredSubCategories(subCategories);
                                }}
                            >
                                ×
                            </button>
                        )}
                    </div>
                </div>

                {/* Sub Categories Grid */}
                <div className="sub-categories-grid">
                    {filteredSubCategories.length > 0 ? (
                        filteredSubCategories.map((subCategory) => (
                            <Link 
                                to={`/${subCategory.url}`}
                                className="category-card" 
                                key={subCategory.id}
                                state={{ id: subCategory.id, mainCategoryId: activeMainCategory }}
                            >
                                <div className="category-image-container">
                                    <ImageWithFallback
                                        src={subCategory.iconUrl}
                                        alt={subCategory.title}
                                        className="category-image"
                                        hideOnError={true}
                                    />
                                </div>
                                <div className="category-content">
                                    <h3>{subCategory.title}</h3>
                                    <div className="category-hover-effect"></div>
                                </div>
                            </Link>
                        ))
                    ) : (
                        <div className="no-results">
                            <svg className="no-results-icon" viewBox="0 0 24 24">
                                <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
                            </svg>
                            <p>لا توجد نتائج للبحث</p>
                        </div>
                    )}
                </div>
            </section>

            <BusinessPrompt
                isOpen={isBusinessPromptOpen}
                onClose={() => setIsBusinessPromptOpen(false)}
                whatsappNumber={homeData.whatsappNumber}
            />
        </div>
    );
};

export default HomePage; 